import React, { useEffect, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import GroupsIcon from "@mui/icons-material/Groups";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import EventIcon from "@mui/icons-material/Event";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import ApartmentIcon from "@mui/icons-material/Apartment";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { CuadrillaCard } from "./CuadrillaCard";
import { getDetalleParteGrupo, updateParteGrupoEstado } from "../../services/apis";
import { formatDateTimeEs } from "../../utils/formatters";

const actualizarAvatarEnDetalle = (detalle, idCuadrilla, idUsuario, imagenUrl) => {
	if (!detalle || detalle?.datos_cuadrilla?.id_cuadrilla !== idCuadrilla) {
		return detalle;
	}

	const actualizarGrupo = (grupo = []) =>
		grupo.map((recolector) =>
			recolector?.id_usuario === idUsuario
				? { ...recolector, imagen_url: imagenUrl }
				: recolector
		);

	return {
		...detalle,
		recolectores: {
			...detalle.recolectores,
			no_comprobados: actualizarGrupo(detalle.recolectores?.no_comprobados),
			comprobados: actualizarGrupo(detalle.recolectores?.comprobados),
			error_comprobacion: actualizarGrupo(detalle.recolectores?.error_comprobacion),
		},
	};
};

const actualizarCheckInformativoEnDetalle = (
	detalle,
	idCuadrilla,
	idRecolectorParte,
	checkInformativo
) => {
	if (!detalle || detalle?.datos_cuadrilla?.id_cuadrilla !== idCuadrilla) {
		return detalle;
	}

	const actualizarGrupo = (grupo = []) =>
		grupo.map((recolector) =>
			Number(recolector?.id_recolector_ptegrupo || 0) === Number(idRecolectorParte || 0)
				? { ...recolector, check_informativo: Number(checkInformativo) }
				: recolector
		);

	return {
		...detalle,
		recolectores: {
			...detalle.recolectores,
			no_comprobados: actualizarGrupo(detalle.recolectores?.no_comprobados),
			comprobados: actualizarGrupo(detalle.recolectores?.comprobados),
			error_comprobacion: actualizarGrupo(detalle.recolectores?.error_comprobacion),
		},
	};
};

export const TablaCuadrillas = ({
	currentCuadrillas,
	setOpen,
	open,
	capabilities,
	onRecolectorAvatarUploaded,
	onEstadoParteUpdated,
	personaReturnState,
	selectedCuadrillas,
	onToggleCuadrillaSelection,
	onToggleCurrentPageSelection,
	allCurrentPageSelected,
	someCurrentPageSelected,
}) => {
	const dialogOpen = Number.isInteger(open);
	const [dialogCuadrillaId, setDialogCuadrillaId] = useState(null);
	const [detalleCuadrilla, setDetalleCuadrilla] = useState(null);
	const [detalleLoading, setDetalleLoading] = useState(false);
	const [detalleError, setDetalleError] = useState("");

	const selectedCuadrilla = useMemo(() => {
		if (!dialogOpen) return null;
		return currentCuadrillas?.[open] || null;
	}, [currentCuadrillas, dialogOpen, open]);

	useEffect(() => {
		const nextCuadrillaId = selectedCuadrilla?.datos_cuadrilla?.id_cuadrilla ?? null;

		if (dialogOpen && nextCuadrillaId) {
			setDialogCuadrillaId(nextCuadrillaId);
		}
	}, [dialogOpen, selectedCuadrilla?.datos_cuadrilla?.id_cuadrilla]);

	useEffect(() => {
		let active = true;
		const idCuadrilla = dialogCuadrillaId;

		if (!dialogOpen || !idCuadrilla) {
			return () => {
				active = false;
			};
		}

		const cargarDetalle = async () => {
			setDetalleLoading(true);
			setDetalleError("");

			try {
				const response = await getDetalleParteGrupo(idCuadrilla);

				if (!active) return;

				if (response?.success) {
					setDetalleCuadrilla(response.data || null);
				} else {
					setDetalleCuadrilla(null);
					setDetalleError(response?.message || "No se pudo cargar el detalle del grupo");
				}
			} catch (error) {
				console.error("Error al cargar detalle de grupo:", error);
				if (active) {
					setDetalleCuadrilla(null);
					setDetalleError("No se pudo cargar el detalle del grupo");
				}
			} finally {
				if (active) {
					setDetalleLoading(false);
				}
			}
		};

		cargarDetalle();

		return () => {
			active = false;
		};
	}, [dialogCuadrillaId, dialogOpen]);

	const cerrarDetalle = () => {
		setOpen(null);
	};

	const handleEstadoChange = async ({ idCuadrilla, field, value }) => {
		try {
			await updateParteGrupoEstado({
				id_cuadrilla: idCuadrilla,
				[field]: Number(value),
			});

			if (typeof onEstadoParteUpdated === "function") {
				onEstadoParteUpdated({
					idCuadrilla,
					changes: {
						[field]: Number(value),
					},
				});
			}
		} catch (error) {
			console.error("Error al actualizar el estado del parte de grupo:", error);
		}
	};

	const handleDetalleExited = () => {
		setDetalleCuadrilla(null);
		setDetalleLoading(false);
		setDetalleError("");
		setDialogCuadrillaId(null);
	};

	const refetchDetalleCuadrilla = async () => {
		const idCuadrilla =
			detalleCuadrilla?.datos_cuadrilla?.id_cuadrilla ||
			selectedCuadrilla?.datos_cuadrilla?.id_cuadrilla ||
			null;

		if (!idCuadrilla) return;

		setDetalleLoading(true);
		setDetalleError("");

		try {
			const response = await getDetalleParteGrupo(idCuadrilla);

			if (response?.success) {
				setDetalleCuadrilla(response.data || null);
			} else {
				setDetalleCuadrilla(null);
				setDetalleError(response?.message || "No se pudo cargar el detalle del grupo");
			}
		} catch (error) {
			console.error("Error al recargar detalle de grupo:", error);
			setDetalleCuadrilla(null);
			setDetalleError("No se pudo cargar el detalle del grupo");
		} finally {
			setDetalleLoading(false);
		}
	};

	const handleRecolectorAvatarUploadedLocal = (payload) => {
		if (typeof onRecolectorAvatarUploaded === "function") {
			onRecolectorAvatarUploaded(payload);
		}

		setDetalleCuadrilla((current) =>
			actualizarAvatarEnDetalle(
				current,
				payload.idCuadrilla,
				payload.idUsuario,
				payload.imagenUrl
			)
		);
	};

	const handleRecolectorCheckInformativoUpdated = ({
		idCuadrilla,
		idRecolectorParte,
		checkInformativo,
	}) => {
		setDetalleCuadrilla((current) =>
			actualizarCheckInformativoEnDetalle(
				current,
				idCuadrilla,
				idRecolectorParte,
				checkInformativo
			)
		);
	};

	const tituloDetalle =
		detalleCuadrilla?.datos_cuadrilla?.id_cuadrilla ||
		dialogCuadrillaId ||
		"";
	const openDetalleByIndex = (index) => {
		setOpen(index);
	};
	const clickableCellSx = {
		cursor: "pointer",
		transition: "background-color 0.2s ease",
		"&:hover": {
			bgcolor: "action.hover",
		},
	};
	const showGrabadoColumn = useMemo(
		() =>
			currentCuadrillas.some(
				(info) =>
					info?.datos_cuadrilla?.grabado !== null &&
					info?.datos_cuadrilla?.grabado !== undefined
			),
		[currentCuadrillas]
	);

	return (
		<>
			<Table stickyHeader sx={{ minWidth: "100%" }} size="small">
				<TableHead>
						<TableRow>
						{capabilities?.canSelectPartes && (
							<TableCell padding="checkbox" align="center">
								<Checkbox
									checked={Boolean(allCurrentPageSelected)}
									indeterminate={!allCurrentPageSelected && Boolean(someCurrentPageSelected)}
									onChange={(event) =>
										onToggleCurrentPageSelection?.(event.target.checked)
									}
									size="small"
								/>
							</TableCell>
						)}
						<TableCell align="left" sx={{ fontSize: 20 }}>
							<GroupWorkIcon sx={{ mb: -1, fontSize: 30 }} color="inherit" /> ID
						</TableCell>
						<TableCell align="left" sx={{ fontSize: 20 }}>
							<GroupWorkIcon sx={{ mb: -1, fontSize: 30 }} color="warning" /> N. Grupo
						</TableCell>
						<TableCell align="left" sx={{ fontSize: 20 }}>
							<EventIcon sx={{ mb: -1, fontSize: 30 }} color="primary" /> Fecha parte de grupo
						</TableCell>
						<TableCell align="left" sx={{ fontSize: 20 }}>
							<ApartmentIcon sx={{ mb: -1, fontSize: 30 }} color="success" /> Empresa
						</TableCell>
						<TableCell align="left" sx={{ fontSize: 20 }}>
							Delegacion
						</TableCell>
						<TableCell align="left" sx={{ fontSize: 20 }}>
							Campaña
						</TableCell>
						<TableCell align="left" sx={{ fontSize: 20 }}>
							<AssignmentIndIcon sx={{ mb: -1, fontSize: 30 }} color="secondary" /> Cabo
						</TableCell>

						{capabilities?.canEditEstado && (
							<TableCell align="left" sx={{ fontSize: 20 }}>
								Validado
							</TableCell>
						)}
						{capabilities?.canEditEstado && (
							<TableCell align="left" sx={{ fontSize: 20 }}>
								Revisado
							</TableCell>
						)}
						{capabilities?.canEditEstado && showGrabadoColumn && (
							<TableCell align="left" sx={{ fontSize: 20 }}>
								Grabado
							</TableCell>
						)}
						<TableCell align="center" sx={{ fontSize: 16, whiteSpace: "nowrap", px: 1 }}>
							<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
								<GroupsIcon sx={{ fontSize: 30 }} color="inherit" />
								<span>Personas</span>
							</Box>
						</TableCell>

						<TableCell align="center" sx={{ fontSize: 16, whiteSpace: "nowrap", px: 1 }}>
							<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
								<GroupsIcon sx={{ fontSize: 30 }} color="secondary" />
								<span>Fichados</span>
							</Box>
						</TableCell>

						<TableCell align="center" sx={{ fontSize: 16, whiteSpace: "nowrap", px: 1 }}>
							<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
								<GroupsIcon sx={{ fontSize: 30 }} color="warning" />
								<span>Pendientes</span>
							</Box>
						</TableCell>

						<TableCell align="center" sx={{ fontSize: 16, whiteSpace: "nowrap", px: 1 }}>
							<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
								<GroupsIcon sx={{ fontSize: 30 }} color="error" />
								<span>No reconocidos</span>
							</Box>
						</TableCell>
					</TableRow>
				</TableHead>

				<TableBody>
					{currentCuadrillas.map((info, index) => {
						const totalRecolectores =
							info.recolectores.no_comprobados.length +
							info.recolectores.comprobados.length +
							info.recolectores.error_comprobacion.length;

						const comprobados = info.recolectores.comprobados.length;
						const noComprobados = info.recolectores.no_comprobados.length;
						const errorComprobacion = info.recolectores.error_comprobacion.length;
						const intentosNoPertenece = Number(
							info?.datos_cuadrilla?.intentos_no_pertenece ?? 0
						);
						const intentosNoRegistrado = Number(
							info?.datos_cuadrilla?.intentos_no_registrado ?? 0
						);
						const noReconocidos =
							errorComprobacion +
							(Number.isFinite(intentosNoPertenece) ? intentosNoPertenece : 0) +
							(Number.isFinite(intentosNoRegistrado) ? intentosNoRegistrado : 0);
						const idCuadrilla = info?.datos_cuadrilla?.id_cuadrilla ?? index;

						return (
							<TableRow key={idCuadrilla}>
								{capabilities?.canSelectPartes && (
									<TableCell padding="checkbox" align="center">
										<Checkbox
											checked={Boolean(selectedCuadrillas?.[idCuadrilla])}
											onChange={() => onToggleCuadrillaSelection?.(idCuadrilla)}
											size="small"
										/>
									</TableCell>
								)}
								<TableCell
									align="center"
									sx={{ fontSize: 17, ...clickableCellSx }}
									onClick={() => openDetalleByIndex(index)}
								>
									{info.datos_cuadrilla.id_cuadrilla}
								</TableCell>
								<TableCell
									align="center"
									sx={{ fontSize: 17, ...clickableCellSx }}
									onClick={() => openDetalleByIndex(index)}
								>
									{info.datos_cuadrilla.nombre_cuadrilla}
								</TableCell>
								<TableCell
									align="left"
									sx={{ fontSize: 17, ...clickableCellSx }}
									onClick={() => openDetalleByIndex(index)}
								>
									{formatDateTimeEs(info.datos_cuadrilla.fecha_cuadrilla)}
								</TableCell>

								<TableCell
									align="left"
									sx={{ fontSize: 17, ...clickableCellSx }}
									onClick={() => openDetalleByIndex(index)}
								>
									{info.datos_cuadrilla.nombre_cliente}
								</TableCell>
								<TableCell
									align="left"
									sx={{ fontSize: 17, ...clickableCellSx }}
									onClick={() => openDetalleByIndex(index)}
								>
									{info.datos_cuadrilla.nombre_delegacion || "Sin delegacion"}
								</TableCell>
								<TableCell
									align="left"
									sx={{ fontSize: 17, ...clickableCellSx }}
									onClick={() => openDetalleByIndex(index)}
								>
									{info.datos_cuadrilla.nombre_campana || "Sin campaña"}
								</TableCell>
								<TableCell
									align="left"
									sx={{ fontSize: 17, ...clickableCellSx }}
									onClick={() => openDetalleByIndex(index)}
								>
									{info.datos_cuadrilla.apellidos_cabo} 	{info.datos_cuadrilla.nombre_cabo}
								</TableCell>
								{capabilities?.canEditEstado && (
									<TableCell align="left" sx={{ fontSize: 17 }}>
										<FormControl variant="standard" fullWidth size="small">
											<InputLabel id={`cuadrilla-validado-${idCuadrilla}`}>Validado</InputLabel>
											<Select
												labelId={`cuadrilla-validado-${idCuadrilla}`}
												id={`cuadrilla-validado-select-${idCuadrilla}`}
												label="Validado"
												value={Number(info?.datos_cuadrilla?.validado ?? 0)}
												onChange={(event) =>
													handleEstadoChange({
														idCuadrilla,
														field: "validado",
														value: event.target.value,
													})
												}
											>
												<MenuItem value={0}>No</MenuItem>
												<MenuItem value={1}>Si</MenuItem>
											</Select>
										</FormControl>
									</TableCell>
								)}
								{capabilities?.canEditEstado && (
									<TableCell align="left" sx={{ fontSize: 17 }}>
										<FormControl variant="standard" fullWidth size="small">
											<InputLabel id={`cuadrilla-revisado-${idCuadrilla}`}>Revisado</InputLabel>
											<Select
												labelId={`cuadrilla-revisado-${idCuadrilla}`}
												id={`cuadrilla-revisado-select-${idCuadrilla}`}
												label="Revisado"
												value={Number(info?.datos_cuadrilla?.revisado ?? 0)}
												onChange={(event) =>
													handleEstadoChange({
														idCuadrilla,
														field: "revisado",
														value: event.target.value,
													})
												}
											>
												<MenuItem value={0}>No</MenuItem>
												<MenuItem value={1}>Si</MenuItem>
											</Select>
										</FormControl>
									</TableCell>
								)}
								{capabilities?.canEditEstado && showGrabadoColumn && (
									<TableCell align="left" sx={{ fontSize: 17 }}>
										{info?.datos_cuadrilla?.grabado !== null &&
										info?.datos_cuadrilla?.grabado !== undefined ? (
											<FormControl variant="standard" fullWidth size="small">
												<InputLabel id={`cuadrilla-grabado-${idCuadrilla}`}>Grabado</InputLabel>
												<Select
													labelId={`cuadrilla-grabado-${idCuadrilla}`}
													id={`cuadrilla-grabado-select-${idCuadrilla}`}
													label="Grabado"
													value={Number(info?.datos_cuadrilla?.grabado ?? 0)}
													onChange={(event) =>
														handleEstadoChange({
															idCuadrilla,
															field: "grabado",
															value: event.target.value,
														})
													}
												>
													<MenuItem value={0}>No</MenuItem>
													<MenuItem value={1}>Si</MenuItem>
												</Select>
											</FormControl>
										) : (
											<Typography variant="body2" color="text.secondary">
												-
											</Typography>
										)}
									</TableCell>
								)}
								<TableCell align="center" sx={{ fontSize: 15, px: 1 }}>
									<Button
										variant="text"
										size="small"
										onClick={() => openDetalleByIndex(index)}
										sx={{ minWidth: 0, px: 1, fontSize: "0.85rem" }}
									>
										{totalRecolectores}
									</Button>
								</TableCell>
								<TableCell align="center" sx={{ fontSize: 15, px: 1 }}>
									<Button
										variant="text"
										size="small"
										onClick={() => openDetalleByIndex(index)}
										sx={{ minWidth: 0, px: 1, fontSize: "0.85rem" }}
									>
										{comprobados}
									</Button>
								</TableCell>
								<TableCell align="center" sx={{ fontSize: 15, px: 1 }}>
									<Button
										variant="text"
										size="small"
										onClick={() => openDetalleByIndex(index)}
										sx={{ minWidth: 0, px: 1, fontSize: "0.85rem" }}
									>
										{noComprobados}
									</Button>
								</TableCell>
								<TableCell align="center" sx={{ fontSize: 15, px: 1 }}>
									<Button
										variant="text"
										size="small"
										onClick={() => openDetalleByIndex(index)}
										sx={{ minWidth: 0, px: 1, fontSize: "0.85rem" }}
									>
										{noReconocidos}
									</Button>
								</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>

			<Dialog
				open={dialogOpen}
				onClose={cerrarDetalle}
				aria-labelledby="detalle-parte-grupo-title"
				fullWidth
				maxWidth="xl"
				keepMounted
				TransitionProps={{ onExited: handleDetalleExited }}
			>
				<DialogTitle id="detalle-parte-grupo-title" component="div">
					<Grid
						container
						direction="row"
						justifyContent="space-between"
						alignItems="center"
						size={{ xs: 12 }}
					>
						<Typography variant="h4">
							Informacion del grupo {tituloDetalle ? `#${tituloDetalle}` : ""}
						</Typography>
						<IconButton size="medium" onClick={cerrarDetalle}>
							<CloseIcon sx={{ fontSize: 30 }} />
						</IconButton>
					</Grid>
				</DialogTitle>

				<DialogContent>
					{detalleLoading && (
						<Box
							sx={{
								minHeight: "35vh",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<CircularProgress />
						</Box>
					)}

					{!detalleLoading && !!detalleError && <Alert severity="error">{detalleError}</Alert>}

					{!detalleLoading && !detalleError && detalleCuadrilla && (
						<CuadrillaCard
							info={detalleCuadrilla}
							parentDialogOpen={dialogOpen}
							capabilities={capabilities}
							onRecolectorAvatarUploaded={handleRecolectorAvatarUploadedLocal}
							onRecolectorCheckInformativoUpdated={handleRecolectorCheckInformativoUpdated}
							onEstadoParteUpdated={onEstadoParteUpdated}
							onRefetchDetalle={refetchDetalleCuadrilla}
							personaReturnState={personaReturnState}
						/>
					)}
				</DialogContent>
			</Dialog>
		</>
	);
};
